import React, { useState, useEffect, useContext, useMemo } from 'react';
import '../main.css';
import './loaddetails.css';
import { getListLoad } from '../apiCalls';
import { StoreContext } from '../Store';
import DataTable from '../DataTable';
import GridTotals from '../GridTotals';
import LoadHeader from './LoadHeader';
import IpaffsModalReadOnly from '../ipaffs/IpaffsModalReadOnly';
import { showCheckIpaffsButton } from '../ipaffs/utils';
import { getHeaderValues } from '../Newload/QAChecklistTab/utils';
import MultipleConsignorsModal from '../Newload/MultipleConsignorsModal/MultipleConsignorsModal';
import { getBulkNeeNorDetailsFromIds } from '../../api/neenor';
import { getCompanyNameFromId } from '../../api/newLoad';
import ExciseModal from '../ExciseModal/ExciseModal';
import '../Spreadsheet/RowNumberColumn/RowNumberColumn.css';

const LoadItems = (props) => {
  const store = useContext(StoreContext);
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({});
  const [requestFailed, setRequestFailed] = useState(false);
  const [showIpaffsModal, setShowIpaffsModal] = useState(false);
  const [isMultipleConsignorsModalOpen, setIsMultipleConsignorsModalOpen] = useState(false);
  const [isExciseModalOpen, setIsExciseModalOpen] = useState(false);
  const invoiceNumber = props.invoicenumber;
  const headerData = props.headerData;

  useEffect(() => {
    const fetch = async () => {
      const data = await getCompanyNameFromId(store.clientID[0]);
      store.clientIDname[1](data[0].companyName);
      store.clientIDEori[1](data[0].eori);
      store.billableType[1](data[0].billableType);
    };
    if (store.clientID[0]) fetch();
  }, [store.clientID[0]]);

  useEffect(() => {
    async function fetchData() {
      try {
        const data = await getListLoad('items', invoiceNumber);
        setItems(data.Newload[0].Items);
        //totals are formatted in their component - src\components\GridTotals.js
        setTotals({
          grossKG: data.Newload[0].grossKG,
          netKG: data.Newload[0].netKG,
          numberItems: data.Newload[0].numberItems,
          loadValue: data.Newload[0].loadValue.toFixed(2),
          itemRows: data.Newload[0].itemRows,
          itemHeaders: getHeaderValues(data.Newload[0].Items, 'loadItems'),
        });
      } catch (err) {
        setRequestFailed(true);
      }
    }
    fetchData();
  }, []);

  const returnData = useMemo(() => {
    const vatOptions = store.VatOptions[0];
    const preOptions = store.PreferentialOptions[0];

    return items?.map((row, index) => {
      const vatOption = vatOptions.find((opt) => parseInt(opt.id) === parseInt(row.VAT));
      const preOpt = preOptions.find((opt) => parseInt(opt.id) === parseInt(row.preferential));

      row.Item = index + 1;
      row.VAT = vatOption ? vatOption.data : row.VAT;
      row.preferential = preOpt ? preOpt.data : row.preferential;
      //format data to always show 2 decimal places; to match consistency in the totals
      row['Gross mass (kg)'] = row['Gross mass (kg)'] ? parseFloat(row['Gross mass (kg)']).toFixed(2) : '-';
      row['Item value'] = row['Item value'] ? parseFloat(row['Item value']).toFixed(2) : '-';
      row['Net mass (kg)'] = row['Net mass (kg)'] ? parseFloat(row['Net mass (kg)']).toFixed(2) : '-';
      row['hsCode'] = row['HS Code (10)'];
      return row;
    });
  }, [items, store.VatOptions, store.PreferentialOptions]);

  const invoiceLines = items.map((item) => {
    const { InvoiceNumber, neeNoridconsignor } = item;
    return { invNumber: InvoiceNumber, consignorId: neeNoridconsignor };
  });

  const fetchConsignorDetails = async (items) => {
    // fetch consignor IDs from the spreadsheet rows - pass them to a new API and populate inside consignorDetails
    const consignorIds = Array.from(new Set(items.map((item) => item.neeNoridconsignor).filter(Boolean)));
    const data = await getBulkNeeNorDetailsFromIds(consignorIds);

    // create object where key is data.id and value is rest of the data
    const detailsObj = {};
    data.forEach((item) => {
      detailsObj[item.id] = {
        ...item,
        addressDetailsComplete: !!item.addressDetailsComplete,
        eori: item.eori,
      };
    });
    store.multipleConsignorDetails[1](detailsObj);

    // for submitted loads we don't need to show errors for consignor / invoices
    store.isMultipleInvoicesValid[1](true);
    store.isMultipleConsignorsValid[1](true);
  };

  useEffect(() => {
    fetchConsignorDetails(items);
  }, [items]);

  if (requestFailed) return <p>Failed!</p>;

  return (
    <div className="floating-box-newload loaditems">
      <IpaffsModalReadOnly
        show={showIpaffsModal}
        onHide={() => setShowIpaffsModal(false)}
        ipaffsCategories={store.ipaffsCategories[0]}
        certificateCount={store.certificateCount[0]}
        data={returnData.filter((i) => i.categoryOption)}
      />
      <MultipleConsignorsModal
        show={isMultipleConsignorsModalOpen}
        invoiceLines={invoiceLines}
        consignorDetails={store.multipleConsignorDetails[0]}
        onHide={() => setIsMultipleConsignorsModalOpen(false)}
        locked
      />
      <ExciseModal
        items={returnData}
        show={isExciseModalOpen}
        onHide={() => setIsExciseModalOpen(false)}
        exciseOptions={store.exciseOptions[0]}
        locked
      />

      <div className="d-flex flex-row justify-content-between">
        <h1 className="floating-box-header-newload loads">Load Items</h1>
        <div className="d-flex flex-column gap-2 justify-content-between align-items-end mb-4">
          <LoadHeader
            portalRef={headerData?.portalRef}
            companyName={headerData?.companyName}
            eoriNumber={headerData?.eoriNumber}
          />
        </div>
      </div>
      <GridTotals
        grossKG={totals.grossKG}
        netKG={totals.netKG}
        value={totals.loadValue}
        itemCount={totals.numberItems}
        rows={totals.itemRows}
        additionalStyles={{ marginTop: '2rem', marginBottom: '1rem' }}
        itemHeaders={totals.itemHeaders}
        billableType={store.billableType[0]}
      />

      <DataTable
        data={returnData}
        sortable
        filterable
        className="table-striped table-bordered"
        showLowerCaseHeaders={true}
      >
        <DataTable.Pagination itemsPerPage={[10, 25, 50]} />
        <DataTable.Headers style={{ backgroundColor: 'white', fontSize: '13px' }} />
        <DataTable.Row style={{ fontSize: '14px' }} />
        <DataTable.Column
          colKey="Item"
          header="Item"
          style={{ textAlign: 'center', width: '4rem' }}
          className="non-copyable-column"
          headerClassName="non-copyable-column"
        />
        {(store.multipleConsignors[0] || store.multipleInvoices[0]) && (
          <DataTable.Column colKey="InvoiceNumber" header="Invoice" />
        )}
        <DataTable.Column colKey="HS Code (10)" header="HS Code" />
        <DataTable.Column colKey="Origin" header="Origin" className="countryOfOrigin" />
        <DataTable.Column colKey="Description of goods" header="Description" className="description" maxWidth={300} />
        <DataTable.Column colKey="No of packages" header="No. Items" />
        <DataTable.Column colKey="Package" header="Type" />
        <DataTable.Column colKey="Gross mass (kg)" header="Gross Mass" style={{ textAlign: 'right' }} />
        <DataTable.Column colKey="Net mass (kg)" header="Net Mass" style={{ textAlign: 'right' }} />
        <DataTable.Column colKey="Item value" header="Value" style={{ textAlign: 'right' }} />
        <DataTable.Column colKey="Qty 2" header="Qty 2" />
        <DataTable.Column colKey="Health cert" header="CHED Ref" />
        <DataTable.Column colKey="Catch cert" header="Catch Cert" />
        <DataTable.Column colKey="Meursing" header="Meursing" />
        <DataTable.Column colKey="Organic" header="Organic" />
        <DataTable.Column colKey="VAT" header="VAT" />
        <DataTable.Column colKey="RGR" header="RGR" />
        <DataTable.Column colKey="preferential" header="Pref" />
      </DataTable>
      <div style={{ flexGrow: 1 }} />
      <div className="d-flex justify-content-between">
        <div className="d-flex justify-content-between gap-3">
          <button className="blue-button-newload stepper-button yellow" onClick={props.prevPage}>
            Details
          </button>
          {showCheckIpaffsButton(store.directionSelected[0]) && (
            <button className="blue-button-newload stepper-button yellow" onClick={() => setShowIpaffsModal(true)}>
              Check IPAFFS
            </button>
          )}
          {store.multipleConsignors[0] && (
            <button
              className={`blue-button-newload stepper-button yellow text-nowrap`}
              type="button"
              onClick={() => setIsMultipleConsignorsModalOpen(true)}
            >
              View Consignors
            </button>
          )}
          <button className="blue-button-newload stepper-button yellow" onClick={() => setIsExciseModalOpen(true)}>
            Excise
          </button>
        </div>
        <button className="blue-button-newload stepper-button yellow" onClick={props.nextPage}>
          Status
        </button>
      </div>
    </div>
  );
};

export default LoadItems;
